import { Container, Geometry, IDestroyOptions, Mesh, Shader, Texture, Ticker, UniformGroup } from "pixi.js";
import { Event } from "event-system";
import Fragment from "../shaders/border-fog.frag";
import Vertex from "../shaders/border-fog.vert";
import { ICanvasScaler } from "by/gamefactory/canvas/ICanvasScaler";
import { RaidResourcePackage } from "../../resources/RaidResourcePackage";
import { SoundsResourcePackageWrapper } from "../../../../common/resources/SoundsResourcePackageWrapper";
import { PromiseCompletionSource } from "../../../../../../shared/async/PromiseCompletionSource";

export class ShieldExplosiveShaderAnimation extends Container {
    private static readonly ANIMATION_CYCLE = 100;
    private vcs: PromiseCompletionSource<void>;
    public readonly onAnimationProgressChange: Event<number>;
    private readonly soundResources: SoundsResourcePackageWrapper;
    private readonly mesh: Mesh<Shader>;
    public isActive: boolean;

    public constructor(canvasScaler: ICanvasScaler, raidResources: RaidResourcePackage, soundResources: SoundsResourcePackageWrapper) {
        super();
        this.soundResources = soundResources;
        this.onAnimationProgressChange = new Event<number>();
        const geometry = new Geometry();
        geometry.addAttribute("aVertexPosition", [-0.5, -0.5, 0.5, -0.5, 0.5, 0.5, -0.5, 0.5], 2);
        geometry.addAttribute("aUvs", [0, 0, 1, 0, 1, 1, 0, 1], 2);
        geometry.addIndex([0, 1, 2, 0, 2, 3]);
        const ratio = 19.5 / 9;
        const uniforms = new UniformGroup({
            u_shield_texture: raidResources.assets_views_raid_atlases_shield_whole_png,
            u_shield_piece_1_texture: raidResources.assets_views_raid_atlases_shield_shield_piece_1_png,
            u_shield_piece_2_texture: raidResources.assets_views_raid_atlases_shield_shield_piece_2_png,
            u_shield_piece_3_texture: raidResources.assets_views_raid_atlases_shield_shield_piece_3_png,
            u_shield_piece_4_texture: raidResources.assets_views_raid_atlases_shield_shield_piece_4_png,
            u_shield_piece_5_texture: raidResources.assets_views_raid_atlases_shield_shield_piece_5_png,
            u_crack_texture: raidResources.assets_views_raid_atlases_shield_shield_crack_png,
            u_time: 0,
            u_ratio: 1 / ratio,
            u_shield_ratio: 766 / 981
        });
        const shader = Shader.from(Vertex, Fragment, uniforms);
        this.mesh = new Mesh(geometry, shader);
        this.mesh.scale.set(canvasScaler.referenceWidth, canvasScaler.referenceHeight);
        this.addChild(this.mesh);
        this.update = this.update.bind(this);
    }

    private update(dt: number): void {
        let u_time = this.mesh.shader.uniforms.u_time;
        u_time += dt * 2;
        if (u_time >= ShieldExplosiveShaderAnimation.ANIMATION_CYCLE) {
            Ticker.shared.remove(this.update);
            this.isActive = false;
            this.vcs.complete();
            return;
        }
        this.mesh.shader.uniforms.u_time = u_time;
    }

    public async play(): Promise<void> {
        this.reset();
        this.vcs = new PromiseCompletionSource();
        Ticker.shared.add(this.update);
        this.isActive = true;
        this.soundResources.assets_sounds_Raid_Correct_Target_mp3.play();
        await this.vcs.result;
    }

    public setProgress(progress: number): void {
        this.mesh.shader.uniforms.u_time = ShieldExplosiveShaderAnimation.ANIMATION_CYCLE * progress;
    }

    private reset(): void {
        this.mesh.shader.uniforms.u_time = 0;
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        Ticker.shared.remove(this.update);
        super.destroy(options);
    }
}
